import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import ReasonService from '../service';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useHistory } from 'react-router-dom';

import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody,
  Button,
  Table
} from 'reactstrap';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import { useRecordTypes } from '../../RecordType/hooks/useRecordTypes';
import FlexSelect from 'components/Inputs/FlexSelect';
import { AddSupervisorNotificationModal } from 'views/status/form/components/AddSupervisorNotificationModal';
import { getSupervisorAlertTypeLabel } from 'views/status/utils/supervisorAlertTypes';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { ScreenPrompt } from 'components/ScreenPrompt';
import ModalConfirmation from '../../../components/Modal/ConfirmationModal';
import ServiceClassificationService from '../../SLA/MatrixSLA/components/serviceClassifications/service';

const Reasons = (props) => {
  const history = useHistory();
  const {
    reset,
    control,
    handleSubmit,
    errors,
    setValue,
    formState,
    getValues,
    watch
  } = useForm();
  const { isDirty } = formState;
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supervisorAlerts, setSupervisorAlerts] = useState([]);
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [serviceClassificationsByReason, setServiceClassificationsByReason] =
    useState([]);
  const [warningMessage, setWarningMessage] = useState('');
  const { recordTypes, isLoadingRecordTypes } = useRecordTypes(
    entitiesName.PROTOCOL
  );

  const watchValueOfRecordTypeIds = watch('recordTypeIds');

  const { id } = props.match.params;

  useEffect(() => {
    if (id) {
      setLoading(true);
      ReasonService.get(id)
        .then((res) => {
          const getReason = res.data.content;
          const recordTypeIds = getReason.recordTypes.map((rt) => rt.id);
          reset({ ...getReason, recordTypeIds });
          setSupervisorAlerts(getReason?.supervisorAlerts || []);
          setIsUpdate(true);
          setLoading(false);
        })
        .catch((err) => {
          const { data } = err.response;
          toast.error(data.message || 'Erro ao listar motivo!', {
            autoClose: 3000,
            closeOnClick: true
          });
          setLoading(false);
        });
    }
  }, [id, reset]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      ServiceClassificationService.getServiceClassificationByReasonIdAndRecordType(
        id
      )
        .then((res) => {
          const data = res.data.content;
          setServiceClassificationsByReason(data);
          setLoading(false);
        })
        .catch((err) => {
          const { data } = err.response;
          toast.error(
            data.message ||
              'Erro ao listar classificações de serviço associadas a esse motivo',
            {
              autoClose: 3000,
              closeOnClick: true
            }
          );
          setLoading(false);
        });
    }
  }, [id]);

  useEffect(() => {
    const data = getValues();
    let message = '';
    if (isUpdate && serviceClassificationsByReason.length > 0) {
      const difference = recordTypes.filter(
        (item) => !data.recordTypeIds.includes(item.id)
      );

      const differenceIds = difference.map((item) => item.id);

      const filteredData = serviceClassificationsByReason.filter((item) =>
        differenceIds.includes(item.recordTypeId)
      );

      message = filteredData
        .map((item) => {
          const recordType = recordTypes.find(
            (rt) => rt.id === item.recordTypeId
          );
          const recordTypeName = recordType ? recordType.name : 'desconhecido';
          const matrixName = item.matrixName;
          const classifications = item.serviceClassifications
            .map((sc) => `<strong>'${sc.name}'</strong>`)
            .join(', ');
          return `</br>O tipo de registro <strong>'${recordTypeName}'</strong> está associado à matriz <strong>'${matrixName}'</strong> que possui as seguintes classificações com o motivo '${data.name}' e/ou seus submotivos: ${classifications} </br>`;
        })
        .join('\n');

      if (filteredData.length > 0) {
        message +=
          '</br> Ao retirar esse tipo de registro, as classificações de serviço associadas serão <strong style="color: red;">Excluidas</strong>. Deseja continuar?';
      }

      setWarningMessage(message);
    }
  }, [
    recordTypes,
    getValues,
    isUpdate,
    serviceClassificationsByReason,
    watchValueOfRecordTypeIds
  ]);

  const onSubmit = async (data, isSubmittingFromModal) => {
    if (isUpdate && !isSubmittingFromModal && warningMessage !== '') {
      setOpenModalConfirmation(true);
      return;
    }
    try {
      const formatedData = { ...data, supervisorAlerts };

      isUpdate
        ? await ReasonService.update(id, formatedData)
        : await ReasonService.create(formatedData);

      history.push('/admin/reason');
      toast.success('Motivo criado com sucesso', {
        autoClose: 3000,
        closeOnClick: true
      });
    } catch (err) {
      const { data } = err.response;
      toast.error(data.message || 'Erro ao cadastrar motivo!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/reason');
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/reason"
        buttonTitle="Voltar para a pagina de motivo"
      />
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Container className="mt--6" fluid>
        <Row className="mb-3">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Motivo</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(() => {
                        onSubmit(getValues(), false);
                      })}
                    >
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">Nome*</label>
                          <Controller
                            as={Input}
                            control={control}
                            name="name"
                            rules={{ required: 'Nome é obrigatório!' }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="name"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Descrição
                          </label>
                          <Controller
                            as={Input}
                            control={control}
                            name="description"
                            defaultValue=""
                          />
                        </Col>
                        <Col md="6" className="mt-2">
                          <label className="form-control-label">
                            Tipos de registro*
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                dataOptions={recordTypes || []}
                                isMulti={true}
                                value={props.value}
                                valueController={setValue}
                                fieldName="recordTypeIds"
                                labelName="name"
                                valueName="id"
                                isClearable={true}
                                disabled={isLoadingRecordTypes}
                                noOptionsMessage={() =>
                                  'Nenhum tipo de registro encontrado'
                                }
                              />
                            )}
                            control={control}
                            name="recordTypeIds"
                            defaultValue=""
                            rules={{
                              required:
                                'Selecione pelo menos um tipo de registro',
                              validate: (value) => {
                                if (!value || !value.length)
                                  return 'Selecione pelo menos um tipo de registro';
                                return true;
                              }
                            }}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="recordTypeIds"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <hr />

                      <Row className="mb-1">
                        <Col md="12">
                          <h3 className="mb-1">Alertas de supervisão</h3>
                        </Col>
                        <Col md="12">
                          <AddSupervisorNotificationModal
                            buttonLabel="Adicionar"
                            onAdded={(addedAlert) => {
                              if (supervisorAlerts.includes(addedAlert)) {
                                toast.error('Já existe um alerta desse tipo');
                                return;
                              }
                              setSupervisorAlerts((prev) => [
                                ...prev,
                                addedAlert
                              ]);
                            }}
                          />
                        </Col>
                      </Row>

                      <Row className="mb-5">
                        <Col>
                          {supervisorAlerts && !!supervisorAlerts.length && (
                            <Table size="sm">
                              <thead>
                                <tr>
                                  <th>Nome</th>
                                  <th>Remover</th>
                                </tr>
                              </thead>
                              <tbody>
                                {supervisorAlerts.map((item, index) => (
                                  <tr key={index}>
                                    <td>{getSupervisorAlertTypeLabel(item)}</td>
                                    <td>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => {
                                          setSupervisorAlerts((prev) =>
                                            prev.filter(
                                              (alert) => alert !== item
                                            )
                                          );
                                        }}
                                      >
                                        <li className="fas fa-trash"></li>
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </Col>
                      </Row>

                      <ReactButton btnColor="confirmation" type="submit">
                        Salvar
                      </ReactButton>

                      <ReactButton
                        btnColor="cancelation"
                        onClick={() => cancelAction()}
                      >
                        Cancelar
                      </ReactButton>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
      {isUpdate && (
        <ModalConfirmation
          modalConfirmationSize="md"
          isModalOpen={openModalConfirmation}
          onModalToggle={setOpenModalConfirmation}
          unmountOnClose={true}
          deleteResource={() => {
            setOpenModalConfirmation(false);
            const data = getValues();
            onSubmit(data, true);
          }}
          message={
            <span>
              ATENÇÃO! <br />
              <span dangerouslySetInnerHTML={{ __html: warningMessage }} />
            </span>
          }
        />
      )}
    </>
  );
};

export default Reasons;
